import { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Link } from "react-router-dom";
import { Provider } from "react-redux";
import { Box, Button, CssBaseline, ThemeProvider, Typography } from "@mui/material";
import { AppLayout } from "./layouts/AppLayout/AppLayout";
import { DefaultLayout } from "./layouts/DefaultLayout";
import { skidattlTheme } from "./themes/skidattlTheme";
import { AuthProvider, RequireAuth, useAuthContext } from "./features/authentication/contexts/AuthContext";
import { store } from "./redux/store";
import ResetPassword from "./features/authentication/pages/ResetPassword";
import LoginPage from "./features/authentication/pages/Login";
import RegisterPage from "./features/authentication/pages/Register";
import ActivatePage from "./features/authentication/pages/ActivateAccount";
import BeaconsIndex from "./features/beacons/pages/BeaconsIndex";
import AnalyticsIndex from "./features/analytics/pages/AnalyticsIndex";
import DashboardIndex from "./features/dashboard/pages/DashboardIndex";
import TeamIndex from "./features/team/pages/TeamIndex";
import Error401 from "./features/errors/Error401";
import ErrorDialog from "./components/ErrorDialog";
import ModerationIndex from "./features/moderation/ModerationIndex";
import UsersIndex from "./features/users/UsersIndex";
import SubscriptionsIndex from "./features/subscriptions/SubscriptionsIndex";
import SubscriptionPage from "./features/authentication/pages/Subscriptions";
import SubscriptionSuccess from "./features/authentication/pages/SubscriptionSuccess";
import AdminIndex from "./features/admin/AdminIndex";
import OAuthCallback from "./features/authentication/pages/OAuthCallback";
import ShareGroupAccept from "./features/beacons/pages/share/ShareGroupAccept";

export default function App() {

  const [permissionError, setPermissionError] = useState<boolean>(false);

  return (
    <div id="wrapper">
      <Provider store={store}>
        <ThemeProvider theme={skidattlTheme}>
          <CssBaseline />

          <AuthProvider>
            <Routes>
              <Route element={<DefaultLayout />}>
                <Route path="/" element={<PublicPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/activate" element={<ActivatePage />} />
                <Route path="/subscribe" element={<SubscriptionPage />} />
                <Route path="/resetPassword" element={<ResetPassword />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                <Route path="/oauth2/callback" element={<OAuthCallback />} />
              </Route>
              <Route element={<AppLayout />}>
                <Route
                  path="/dashboard"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <DashboardIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/beacons/:view?/:group_id?"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <BeaconsIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/beacons/:group_id/editor/:beacon_id?"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <BeaconsIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:tab?/:view?/:group_id?"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <BeaconsIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/beacons/create"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <BeaconsIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <AnalyticsIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/team"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <TeamIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/moderation"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <ModerationIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <AdminIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <UsersIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <SubscriptionsIndex />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/acceptshare"
                  element={
                    <RequireAuth setPermissionError={setPermissionError}>
                      <ShareGroupAccept />
                    </RequireAuth>
                  }
                />
              </Route>
              
              {/* TODO: 404 */}
              <Route path='*' element={<NotFound />} />
              <Route path='/unauthorized' element={<Error401 />} />
            </Routes>
          </AuthProvider>

          <ErrorDialog
            open={permissionError}
            errorTitle="Access Denied"
            errorMessage={"You do not have permission to access the Skidattl business portal. Contact support@skidattl.com to enrol in the Skidattl Business Program."}
            onClose={() => setPermissionError(false)}
          />

        </ThemeProvider>
      </Provider>
    </div>
  );
}

export const googleLogin = () => {
    
  // const clientId = process.env.REACT_APP_AMAZON_COGNITO_CLIENT_ID;
  // Use hardcode temporarily
  const clientId = "1kq9a2b4tbn71dbtekh64o8fl";
  const host = window.location.host;

  var redirectUri = `https://${host}/oauth2/callback`;

  if (host.includes("localhost")) {
    redirectUri = `http://${host}/oauth2/callback`;
  }

  const url = `https://login.skidattl.com/login?client_id=${clientId}&response_type=code&scope=openid+email+aws.cognito.signin.user.admin&redirect_uri=${redirectUri}`;

  window.location.href = url;

};

function PublicPage() {

  let auth = useAuthContext();
  let navigate = useNavigate();

  useEffect(() => {
    // If the user is already logged in
    if (auth.user) {
      // Send them to the dashboard
        navigate("/beacons", { replace: true });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user]);

  return (
    <Box display='flex' alignItems='center' justifyContent='center' sx={{
      backgroundColor: 'var(--brand-tertiary)',
      height: '100vh',
      backgroundPosition: 'bottom right',
      backgroundSize: 'auto 80%, 100%',
      backgroundRepeat: 'no-repeat',
    }}>
      <Box display='flex' flexDirection='column' alignItems='center' sx={{ gap: '2rem' }}>
        <img src={"/skidattl-logo-lightbkg.svg"} width={160} alt="Skidattl Logo" />
        <Box display='flex' alignItems='center' justifyContent='center' sx={{ gap: '2rem' }} mt={16}>
          <Button variant='contained' component={Link} to={'/login'} color="primary" sx={{ minWidth: '8rem' }}>Log In</Button>
          <Button variant='outlined' component={Link} to={'/register'} color="secondary" sx={{ minWidth: '8rem' }}>Sign Up</Button>
        </Box>
        <Button
          type="button"
          size="medium"
          fullWidth
          variant="contained"
          onClick={googleLogin}
        >
          Sign in with SSO
        </Button>
      </Box>
    </Box>
  );
}

function NotFound() {
  return (
    <Box display='flex' alignItems='center' justifyContent='center' style={{ background: 'var(--brand-tertiary)', height: '100vh' }}>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Typography variant="h1" color="secondary" textAlign={'center'} mb={2}
          sx={{
            backgroundImage: 'radial-gradient(circle farthest-side at top left, var(--brand-primary) 0%, var(--brand-secondary) 100%)',
            backgroundClip: 'text',
            textFillColor: 'transparent'
          }}
        >404</Typography>
        <Typography variant="body1" color="white" textAlign={'center'}>Page not Found</Typography>

        {/* TODO: Check auth status an provide appropriate guidance */}
        <Box display='flex' alignItems='center' justifyContent='center' style={{ background: 'var(--brand-tertiary)', gap: '2rem' }} mt={16}>
          <Button variant='contained' component={Link} to={'/login'} sx={{ minWidth: '8rem' }}>Log In</Button>
          <Button variant='outlined' component={Link} to={'/register'} color="secondary" sx={{ minWidth: '8rem' }}>Sign Up</Button>
        </Box>
      </Box>
    </Box>
  );
}
